import React, { useState, useEffect } from 'react';
import { useProject } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { Image, Video, FileText, Sparkles, Loader2, Download, Trash2, AlertTriangle } from 'lucide-react';

const Marketing = () => {
  const { currentProjectId, projectInfo } = useProject();
  const { api } = useAuth();
  const [content, setContent] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [contentType, setContentType] = useState('image');
  const [channel, setChannel] = useState('social');
  const [generatedContent, setGeneratedContent] = useState(null);

  const fetchData = async () => {
    if (!currentProjectId) return;
    try {
      const [contentData, usageData] = await Promise.all([
        api(`/api/content/?project_id=${currentProjectId}`),
        api(`/api/usage/today?project_id=${currentProjectId}`)
      ]);
      setContent(contentData);
      setUsage(usageData);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [currentProjectId]);

  const generate = async () => {
    if (!prompt.trim()) {
      toast.error('Prompt is required');
      return;
    }

    setGenerating(true);
    setGeneratedContent(null);

    try {
      const response = await api('/api/content/generate', {
        method: 'POST',
        body: JSON.stringify({
          project_id: currentProjectId,
          content_type: contentType,
          prompt,
          channel
        })
      });

      setGeneratedContent(response);
      toast.success('Content generated');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const deleteContent = async (contentId) => {
    try {
      await api(`/api/content/${contentId}`, { method: 'DELETE' });
      toast.success('Deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div data-testid="marketing-page">
      <div className="mb-8">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Marketing Studio</h1>
        <p className="text-muted-foreground">Generate marketing content for {projectInfo.name}</p>
      </div>

      {/* Usage Limits */}
      {usage && (
        <Card className="bg-card border-border card-glow mb-6">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground flex items-center gap-1"><Image className="w-3 h-3" /> Images</span>
                  <span>{usage.images_generated || 0} / {usage.caps?.images || 30}</span>
                </div>
                <Progress value={((usage.images_generated || 0) / (usage.caps?.images || 30)) * 100} className="h-1.5" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground flex items-center gap-1"><Video className="w-3 h-3" /> Videos</span>
                  <span className={usage.videos_generated >= usage.caps?.videos ? 'text-red-400' : ''}>
                    {usage.videos_generated || 0} / {usage.caps?.videos || 2}
                  </span>
                </div>
                <Progress 
                  value={((usage.videos_generated || 0) / (usage.caps?.videos || 2)) * 100} 
                  className={`h-1.5 ${usage.videos_generated >= usage.caps?.videos ? '[&>div]:bg-red-500' : ''}`}
                />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-muted-foreground">Tokens</span>
                  <span>{(usage.tokens_used || 0).toLocaleString()}</span>
                </div>
                <Progress value={((usage.tokens_used || 0) / (usage.caps?.tokens || 100000)) * 100} className="h-1.5" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Generator */}
      <Card className="bg-card border-border card-glow mb-6">
        <CardContent className="p-6">
          <Tabs value={contentType} onValueChange={setContentType}>
            <TabsList className="grid w-full grid-cols-3 mb-4">
              <TabsTrigger value="image" data-testid="tab-image"><Image className="w-4 h-4 mr-1" /> Image</TabsTrigger>
              <TabsTrigger value="video" data-testid="tab-video"><Video className="w-4 h-4 mr-1" /> Video</TabsTrigger>
              <TabsTrigger value="social_post" data-testid="tab-post"><FileText className="w-4 h-4 mr-1" /> Post</TabsTrigger>
            </TabsList>

            <div className="space-y-4">
              <Textarea
                placeholder={
                  contentType === 'image' ? `Create a marketing image for ${projectInfo.name}...` :
                  contentType === 'video' ? 'Describe the video concept...' :
                  'Enter the topic for your post...'
                }
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                data-testid="content-prompt"
              />

              <div className="flex gap-4">
                <Select value={channel} onValueChange={setChannel}>
                  <SelectTrigger className="w-40" data-testid="channel-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="social">Social Media</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="ad">Paid Ads</SelectItem>
                    <SelectItem value="website">Website</SelectItem>
                  </SelectContent>
                </Select>

                <Button 
                  onClick={generate}
                  disabled={generating || !prompt.trim()}
                  className="flex-1"
                  data-testid="generate-btn"
                >
                  {generating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
                  ) : (
                    <><Sparkles className="w-4 h-4 mr-2" /> Generate</>
                  )}
                </Button>
              </div>

              {contentType === 'video' && (
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Video generation creates scripts and storyboards. Sora rendering available when configured.
                </p>
              )}
            </div>
          </Tabs>

          {/* Generated Preview */}
          {generatedContent && (
            <div className="mt-6 p-4 bg-black/30 rounded-lg border border-border">
              <h3 className="font-medium mb-3">Generated Content</h3>
              {generatedContent.image_base64 && (
                <img 
                  src={`data:image/png;base64,${generatedContent.image_base64}`}
                  alt="Generated"
                  className="max-h-64 rounded mb-2"
                  data-testid="generated-image"
                />
              )}
              {generatedContent.video_production_package && (
                <pre className="text-xs text-green-400 bg-black/40 p-3 rounded overflow-auto max-h-64" data-testid="generated-video">
                  {JSON.stringify(generatedContent.video_production_package, null, 2)}
                </pre>
              )}
              {generatedContent.content && (
                <div className="text-sm whitespace-pre-wrap" data-testid="generated-post">
                  {generatedContent.content}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Content Library */}
      <Card className="bg-card border-border card-glow">
        <CardHeader>
          <CardTitle className="text-base">Content Library ({content.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : content.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Sparkles className="w-10 h-10 mx-auto mb-3 opacity-50" />
              <p>No content yet. Generate some!</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {content.slice(0, 12).map((item) => (
                <div key={item.content_id} className="relative group">
                  <div className="aspect-square bg-black/30 rounded-lg overflow-hidden border border-border">
                    {(item.image_url || item.image_base64 || item.file_base64) ? (
                      <img 
                        src={
                          item.image_url
                            ? item.image_url
                            : `data:image/png;base64,${item.image_base64 || item.file_base64}`
                        }
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={e => {
                          const b64 = item.image_base64 || item.file_base64;
                          if (b64 && e.target.src !== `data:image/png;base64,${b64}`) {
                            e.target.src = `data:image/png;base64,${b64}`;
                          }
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        {item.content_type === 'video' || item.content_type === 'video_script' ? (
                          <Video className="w-6 h-6 text-muted-foreground" />
                        ) : (
                          <FileText className="w-6 h-6 text-muted-foreground" />
                        )}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => deleteContent(item.content_id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                  <div className="mt-1 text-xs truncate text-muted-foreground">{item.title}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Marketing;
